import { InteractionType } from '@azure/msal-browser';
import { useIsAuthenticated, useMsalAuthentication } from '@azure/msal-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { type FC, type ReactElement, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { activateSubscription, getSubscriptionById } from '../api/subscription';
import { TroubleshootingSteps } from '../components/TroubleshootingSteps';
import { UserHeader } from '../components/UserHeader';
import { useMsalIdToken } from '../hooks/useMsalIdToken';
import { useSubscriptionPolling } from '../hooks/useSubscriptionPolling';

const SubscriptionDetail: FC = (): ReactElement => {
  const { id: subscriptionId } = useParams<{ id: string }>();
  const [isActivating, setIsActivating] = useState(false);

  // Initialize MSAL authentication - automatically redirects if not authenticated
  const isAuthenticated = useIsAuthenticated();
  useMsalAuthentication(InteractionType.Redirect, {
    scopes: ['openid', 'profile', 'email'],
  });

  const msalToken = useMsalIdToken();

  const subscriptionQuery = useQuery({
    enabled: !!msalToken && !!subscriptionId,
    queryKey: ['subscription', subscriptionId],
    queryFn: () => (msalToken && subscriptionId ? getSubscriptionById({ msalToken, subscriptionId }) : undefined),
  });

  const subscription = subscriptionQuery.data?.data.subscription;
  const status = subscription?.saasSubscriptionStatus;

  // Activation mutation
  const activateSubscriptionMutation = useMutation({
    mutationFn: (data: { msalToken: string; subscriptionId: string }) => activateSubscription(data),
    onSuccess: async () => {
      setIsActivating(true);
    },
  });

  // Poll for activation status change
  useSubscriptionPolling({
    subscriptionId,
    shouldPoll: isActivating,
    currentStatus: status,
    expectedStatuses: ['Subscribed'],
  });

  const handleActivate = () => {
    if (msalToken && subscriptionId) {
      activateSubscriptionMutation.mutate({ msalToken, subscriptionId });
    }
  };

  // Show loading state while authentication is in progress
  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
          <p className="text-sm text-gray-600">Authenticating...</p>
        </div>
      </div>
    );
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Subscribed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'PendingFulfillmentStart':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Suspended':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Unsubscribed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PendingFulfillmentStart':
        return 'Pending';
      default:
        return status;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-5">
      <div className="mx-auto max-w-4xl">
        <div className="rounded-lg bg-white p-10 shadow-md">
          <UserHeader />

          <div className="mb-6">
            <Link to="/" className="text-sm font-medium text-indigo-600 hover:text-indigo-800">
              ← Back to Subscriptions
            </Link>
          </div>

          {/* Loading state */}
          {subscriptionQuery.isLoading && (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
              <p className="text-sm text-gray-600">Loading subscription details...</p>
            </div>
          )}

          {/* Error state */}
          {subscriptionQuery.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
              <p className="font-medium">Failed to load subscription details</p>
              <p className="mt-1 text-sm">This subscription may not exist or you don't have access to it.</p>
              <TroubleshootingSteps />
            </div>
          )}

          {/* Subscription details */}
          {subscription && (
            <>
              {/* Hero Section */}
              <div className="mb-8">
                <h1 className="mb-2 text-3xl font-bold text-gray-900">{subscription.name}</h1>
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-block rounded-full border px-4 py-1.5 text-base font-semibold ${getStatusBadgeClass(subscription.saasSubscriptionStatus)}`}
                  >
                    {getStatusLabel(subscription.saasSubscriptionStatus)}
                  </span>
                  <span className="text-sm text-gray-500">Created {formatDate(subscription.created)}</span>
                </div>
              </div>

              {/* Activation Section - Only show if status is pending */}
              {status === 'PendingFulfillmentStart' && !isActivating && (
                <div className="mb-8">
                  <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-6">
                    <h2 className="mb-4 text-xl font-semibold text-gray-900">Activate Your Subscription</h2>
                    <p className="mb-4 text-sm text-gray-700">
                      Click the button below to activate your subscription and start using the service.
                    </p>
                    <button
                      className="w-full rounded-lg bg-indigo-600 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                      onClick={handleActivate}
                      disabled={activateSubscriptionMutation.isPending}
                    >
                      {activateSubscriptionMutation.isPending ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-white"></div>
                          Activating...
                        </span>
                      ) : (
                        'Activate Subscription'
                      )}
                    </button>
                    {activateSubscriptionMutation.isError && (
                      <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-6 text-red-800">
                        <p className="mb-3 text-lg font-semibold">We couldn't activate this subscription.</p>
                        <TroubleshootingSteps />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Activating State */}
              {isActivating && status === 'PendingFulfillmentStart' && (
                <div className="mb-8">
                  <div className="rounded-lg border-2 border-blue-300 bg-blue-50 p-6">
                    <div className="flex items-start gap-4">
                      <div className="mt-1 h-8 w-8 flex-shrink-0 animate-spin rounded-full border-b-2 border-blue-700"></div>
                      <div>
                        <p className="mb-2 text-lg font-semibold text-blue-900">Activating your subscription...</p>
                        <p className="text-sm text-blue-800">
                          This may take a few moments. You can safely close this window now.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Activated Success State */}
              {status === 'Subscribed' && isActivating && (
                <div className="mb-8">
                  <div className="rounded-lg border-2 border-green-300 bg-green-50 p-6">
                    <div className="text-center">
                      <div className="mb-3 text-4xl">🎉</div>
                      <p className="mb-2 text-xl font-bold text-green-900">Subscription Activated!</p>
                      <p className="text-sm text-green-800">Your subscription is now active and ready to use.</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-8">
                {/* Priority Information - Billing & Term */}
                <div>
                  <h2 className="mb-4 text-xl font-semibold text-gray-900">Billing & Subscription Period</h2>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <div className="rounded-lg border border-gray-200 bg-white p-5">
                      <div className="mb-2 text-xs font-medium tracking-wide text-gray-500 uppercase">
                        Billing Cycle
                      </div>
                      <div className="text-xl font-bold text-gray-900">
                        {subscription.term.termUnit === 'P1M' ? 'Monthly' : 'Yearly'}
                      </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-5">
                      <div className="mb-2 text-xs font-medium tracking-wide text-gray-500 uppercase">Start Date</div>
                      <div className="text-xl font-bold text-gray-900">{formatDate(subscription.term.startDate)}</div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-5">
                      <div className="mb-2 text-xs font-medium tracking-wide text-gray-500 uppercase">End Date</div>
                      <div className="text-xl font-bold text-gray-900">{formatDate(subscription.term.endDate)}</div>
                    </div>
                    {subscription.quantity && (
                      <div className="rounded-lg border border-gray-200 bg-white p-5">
                        <div className="mb-2 text-xs font-medium tracking-wide text-gray-500 uppercase">Quantity</div>
                        <div className="text-xl font-bold text-gray-900">{subscription.quantity}</div>
                        <div className="mt-1 text-xs text-gray-500">seats</div>
                      </div>
                    )}
                    {subscription.isFreeTrial && (
                      <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-5">
                        <div className="mb-2 text-xs font-medium tracking-wide text-indigo-600 uppercase">
                          Free Trial
                        </div>
                        <div className="text-xl font-bold text-indigo-700">Active</div>
                        <div className="mt-1 text-xs text-indigo-600">trial period</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Subscription Details */}
                <div>
                  <h2 className="mb-4 text-xl font-semibold text-gray-900">Subscription Details</h2>
                  <div className="rounded-lg border border-gray-200 bg-white p-6">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <div className="mb-1 text-sm font-medium text-gray-500">Subscription ID</div>
                        <div className="font-mono text-sm break-all text-gray-900">{subscription.id}</div>
                      </div>
                      <div>
                        <div className="mb-1 text-sm font-medium text-gray-500">Offer ID</div>
                        <div className="font-mono text-sm break-all text-gray-900">{subscription.offerId}</div>
                      </div>
                      <div>
                        <div className="mb-1 text-sm font-medium text-gray-500">Plan ID</div>
                        <div className="font-mono text-sm break-all text-gray-900">{subscription.planId}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account & Access */}
                <div>
                  <h2 className="mb-4 text-xl font-semibold text-gray-900">Account & Access</h2>
                  <div className="rounded-lg border border-gray-200 bg-white p-6">
                    {/* Beneficiary */}
                    <div className="mb-6">
                      <h3 className="mb-3 text-base font-semibold text-gray-900">Beneficiary</h3>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                          <div className="mb-1 text-sm font-medium text-gray-500">Email</div>
                          <div className="text-sm text-gray-900">{subscription.beneficiary.emailId}</div>
                        </div>
                        <div>
                          <div className="mb-1 text-sm font-medium text-gray-500">Tenant ID</div>
                          <div className="font-mono text-xs break-all text-gray-900">
                            {subscription.beneficiary.tenantId}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Purchaser (only if different) */}
                    {subscription.purchaser.emailId !== subscription.beneficiary.emailId && (
                      <div className="mb-6 border-t border-gray-200 pt-6">
                        <h3 className="mb-3 text-base font-semibold text-gray-900">
                          Purchaser <span className="text-sm font-normal text-gray-500">(CSP Scenario)</span>
                        </h3>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <div>
                            <div className="mb-1 text-sm font-medium text-gray-500">Email</div>
                            <div className="text-sm text-gray-900">{subscription.purchaser.emailId}</div>
                          </div>
                          <div>
                            <div className="mb-1 text-sm font-medium text-gray-500">Tenant ID</div>
                            <div className="font-mono text-xs break-all text-gray-900">
                              {subscription.purchaser.tenantId}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Allowed Operations */}
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="mb-3 text-base font-semibold text-gray-900">Permissions</h3>
                      <div>
                        <div className="mb-2 text-sm font-medium text-gray-500">Allowed Customer Operations</div>
                        <div className="flex flex-wrap gap-2">
                          {subscription.allowedCustomerOperations.map((operation) => (
                            <span
                              key={operation}
                              className="rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-sm font-medium text-gray-800"
                            >
                              {operation}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Technical Details */}
                <div>
                  <h2 className="mb-4 text-xl font-semibold text-gray-900">Technical Details</h2>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <div className="mb-1 text-sm font-medium text-gray-500">Beneficiary Object ID</div>
                        <div className="font-mono text-xs break-all text-gray-700">
                          {subscription.beneficiary.objectId}
                        </div>
                      </div>
                      <div>
                        <div className="mb-1 text-sm font-medium text-gray-500">Beneficiary PUID</div>
                        <div className="font-mono text-xs break-all text-gray-700">{subscription.beneficiary.puid}</div>
                      </div>
                      {subscription.purchaser.emailId !== subscription.beneficiary.emailId && (
                        <>
                          <div>
                            <div className="mb-1 text-sm font-medium text-gray-500">Purchaser Object ID</div>
                            <div className="font-mono text-xs break-all text-gray-700">
                              {subscription.purchaser.objectId}
                            </div>
                          </div>
                          <div>
                            <div className="mb-1 text-sm font-medium text-gray-500">Purchaser PUID</div>
                            <div className="font-mono text-xs break-all text-gray-700">
                              {subscription.purchaser.puid}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(SubscriptionDetail);
